/* ==========================================================================
   Quick Look — questions, and the read they produce.
   Question set and flow follow quick-look.md; the results screen follows
   quick-look-results.md. Wording and logic live here so they can be edited
   without touching layout.

   Principles from the spec that this file has to honour:
   - Tap to answer. No typing anywhere except the single optional box at the end.
   - The frame is the gap between where the business is and where it could be.
     A problem is one kind of gap, and it is written as upside not yet captured —
     never as a diagnosis of what is wrong. Roughly 60/40 toward opportunity.
   - Lead with where the owner said they want to go, then the openings that get
     them there.
   - No scores, grades, levels or gauges reach the owner. Ranking happens in
     here only, to decide which two or three areas are worth naming. The moment
     a rank is shown it becomes a report card, which is the one thing this
     screen must not be.
   - Every opportunity must be usable without hiring us. `action` is the free
     value, and it has to be real advice, not a teaser for the paid engagement.
   - The read is personalised. If it could be printed on a poster it is too
     generic and the trust it was meant to build inverts.

   AN ANSWER IS A KEY, NOT A POSITION.
   Every option carries its own `key`, and that key is what gets stored, scored,
   quoted and sent to the server. Options were once stored by their index, which
   meant the list order was load-bearing: reordering options, or inserting one in
   the middle, silently changed what every answer already in a browser meant.
   Segment variants made it worse — each one had to keep the option order aligned
   with the default's or an area would surface for owners whose own answer ruled
   it out.

   With keys, order is presentation and nothing else. Options can be reordered,
   inserted or reworded freely, and a variant can carry a different set entirely
   or reuse a key where the answer means the same thing and only the phrasing
   changes.

   It also means there is nothing to version. The site decides which questions to
   ask; the store is a bag of answers it looks in on the way past. A key that
   matches an option fills that question in, and one that does not — because the
   option was renamed, or the record predates all of this — leaves the question
   unanswered and it gets asked. Nothing has to be migrated and nothing has to be
   thrown away, so there is no constant to remember to bump.
   ========================================================================== */

/* The openings a Quick Look can surface. Named as things to gain rather than
   things that are broken, because that is the frame the whole tool runs on. */
export const AREAS = {
    capability: 'Something you can’t offer yet',
    ceiling: 'Tools that cap what you can do',
    flow: 'Information moving by hand',
    visibility: 'A clear view of how you’re doing',
    resilience: 'Work that depends on one person'
};

/* Business types that get their own options downstream, keyed by the answer to
   the first question. A startup founder answering the default set is asked how
   information moves between systems they have not built, and how easily they can
   see how the business is doing before it has traded — four of the seven core
   questions land somewhere between awkward and meaningless, which is well past
   what a "does not apply" escape can paper over.

   The fork is in the options, not in the flow. Same questions in the same order,
   same scoring, same length — a startup is just asked each one in terms that
   describe where they actually are.

   The value is the variant name, so two business types can share one variant
   when their situation is the same. Adding a segment is a data change. */
const SEGMENTS = { startup: 'startup' };

export const segmentFor = answers => SEGMENTS[answers && answers.business] || null;

/* A question as one owner should see it. A variant overrides whatever it names
   and inherits the rest, so one that only changes wording does not restate the
   scoring. */
export const resolveQuestion = (question, answers) => {
    const segment = segmentFor(answers);
    const variant = segment && question.variants ? question.variants[segment] : null;
    return variant ? { ...question, ...variant } : question;
};

/* The option an owner picked, or undefined if their stored answer names a key
   this question no longer offers. Every caller has to cope with undefined —
   that is what makes a renamed option cost one answer rather than the record. */
export const findOption = (question, key) => question.options.find(option => option.key === key);

/* What the site holds for a question, once it has checked that the key still
   names something. An answer nobody recognises is not an answer. */
export const answerFor = (question, answers) => {
    const key = answers && answers[question.id];
    return key && findOption(resolveQuestion(question, answers), key) ? key : undefined;
};

/* Where to put someone down in a set of questions: the first one we do not have
   a usable answer for, or -1 when they are all in. Derived rather than stored,
   so a question added, removed or renamed since their last visit sorts itself
   out instead of leaving a saved position pointing at the wrong screen. */
export const firstUnanswered = (questions, answers) =>
    questions.findIndex(question => !answerFor(question, answers));

export const coreAnswered = answers => firstUnanswered(CORE_QUESTIONS, answers) === -1;

/* Each option carries a `weight` for the question's own `area`, or a `scores`
   map when the options point at different areas. `area: null` means context
   only — it still shapes the research file and the wording of the read. */
export const CORE_QUESTIONS = [{
    id: 'business',
    area: null,
    question: 'What kind of business do you run?',
    options: [
        { key: 'service', label: 'Service business (trades, home services, field work)' },
        { key: 'franchise', label: 'Franchise or multi-location operation' },
        { key: 'professional', label: 'Professional services (agency, consulting, firm)' },
        { key: 'retail', label: 'Retail, e-commerce, or product business' },
        { key: 'startup', label: 'Startup building something new' },
        { key: 'other', label: 'Other' }
    ]
},{
    id: 'headcount',
    area: null,
    question: 'Roughly how many people work in your business?',
    options: [
        { key: 'under_5', label: 'Just me / under 5' },
        { key: 'to_20', label: '5 to 20' },
        { key: 'to_50', label: '20 to 50' },
        { key: 'to_100', label: '50 to 100' },
        { key: 'over_100', label: 'Over 100' }
    ]
},{
    id: 'horizon',
    area: null,
    question: 'When you picture your business a year from now, what would make the biggest difference?',
    options: [
        { key: 'more_customers', label: 'Taking on more customers without more chaos', scores: { flow: 2, visibility: 1 } },
        { key: 'new_offering', label: 'Offering something new we can’t offer today', scores: { capability: 3 } },
        { key: 'more_from_tools', label: 'Getting more out of the tools and data we already have', scores: { ceiling: 2, visibility: 2 } },
        { key: 'free_up_time', label: 'Freeing up my time and my team’s time', scores: { flow: 2, resilience: 1 } },
        { key: 'run_smoothly', label: 'Running more smoothly across locations or the whole team', scores: { flow: 2, visibility: 1 } }
    ],
    variants: {
        /* "More customers without more chaos" and "across locations" describe a
           year a startup is not having. These are different answers rather than
           different wording, so they carry their own keys. */
        startup: {
            question: 'When you picture this a year from now, what would make the biggest difference?',
            options: [
                { key: 'build_it', label: 'Having it built and in front of real users', scores: { capability: 3 } },
                { key: 'prove_it', label: 'Knowing the idea works before we spend more on it', scores: { visibility: 3 } },
                { key: 'charge_for_it', label: 'Being able to charge for it', scores: { capability: 2, visibility: 1 } },
                { key: 'less_on_me', label: 'Not having every part of it depend on me', scores: { resilience: 2, flow: 1 } },
                { key: 'show_investors', label: 'Something solid enough to show investors or partners', scores: { capability: 2, ceiling: 1 } }
            ]
        }
    }
},{
    id: 'ambition',
    area: 'capability',
    question: 'Is there something you’ve wanted to build, offer, or improve but haven’t gotten to yet?',
    options: [
        { key: 'priority', label: 'Yes, and it’s a real priority', weight: 3 },
        { key: 'pushed_aside', label: 'Yes, but it keeps getting pushed aside', weight: 3 },
        { key: 'half_formed', label: 'Sort of, an idea that isn’t fully formed yet', weight: 2 },
        { key: 'not_really', label: 'Not really, I’m focused on running what we have', weight: 0 }
    ]
},{
    id: 'ceiling',
    area: 'ceiling',
    question: 'How much of what you’d like to do is held back by the software or tools you have now?',
    options: [
        { key: 'real_ceiling', label: 'A lot, our tools are a real ceiling', weight: 3 },
        { key: 'work_around', label: 'Somewhat, we work around their limits', weight: 2 },
        { key: 'some_gaps', label: 'A little, mostly fine with some gaps', weight: 1 },
        { key: 'not_sure', label: 'Not sure, I haven’t thought about it that way', weight: 1 }
    ],
    variants: {
        /* There is no stack to be held back by yet — for a startup the same
           question is how far the idea sits from what they can put together.
           The four answers mean what the defaults mean, so they keep the same
           keys and only the wording changes. */
        startup: {
            question: 'How much of what you want to build is beyond what you can put together right now?',
            options: [
                { key: 'real_ceiling', label: 'All of it. We know what we want, we just can’t build it', weight: 3 },
                { key: 'work_around', label: 'Most of it. We have something rough that won’t hold up', weight: 2 },
                { key: 'some_gaps', label: 'Some of it. We’re piecing it together with off-the-shelf tools', weight: 1 },
                { key: 'not_sure', label: 'Hard to say. We’re still working out what it needs to do', weight: 1 }
            ]
        }
    }
},{
    id: 'movement',
    area: 'flow',
    question: 'When the same information needs to live in more than one of your systems, how does it get there?',
    hint: 'A customer detail, an order, a job.',
    options: [
        { key: 'syncs', label: 'It syncs automatically', weight: 0 },
        { key: 'by_hand', label: 'Someone copies it over by hand', weight: 2 },
        { key: 're_enter', label: 'We re-enter it in multiple places', weight: 3 },
        { key: 'doesnt_arrive', label: 'Honestly, it often just doesn’t make it everywhere', weight: 3 }
    ],
    variants: {
        /* Often there is nothing to sync yet, and asking anyway tells a founder
           the tool was not built for them. "Not much yet" is a real answer that
           carries signal — they are pre-infrastructure — rather than an escape
           from the question. These are genuinely different answers from the
           defaults, so they carry their own keys. */
        startup: {
            question: 'What are you running the business on right now?',
            hint: 'However you’re keeping track of things today.',
            options: [
                { key: 'nothing_yet', label: 'Not much yet, we’re still building', weight: 0 },
                { key: 'tools_talk', label: 'Off-the-shelf tools that mostly talk to each other', weight: 1 },
                { key: 'spreadsheets', label: 'Spreadsheets and docs we update by hand', weight: 3 },
                { key: 'held_together', label: 'A bit of everything, held together by hand', weight: 3 }
            ]
        }
    }
},{
    id: 'reporting',
    area: 'visibility',
    question: 'When you want a clear answer about how the business is doing, how easily can you get it?',
    options: [
        { key: 'clear_view', label: 'Instantly, I have a clear view', weight: 0 },
        { key: 'takes_digging', label: 'I can get it, but it takes some digging', weight: 1 },
        { key: 'by_hand', label: 'I have to ask someone or pull it together by hand', weight: 2 },
        { key: 'gut_feel', label: 'I mostly go on gut feel', weight: 3 }
    ],
    variants: {
        // nothing has traded yet, so the question is whether the thing works
        // rather than how the business is doing. Same four answers, same keys.
        startup: {
            question: 'When you want to know whether this is working, how easily can you find out?',
            options: [
                { key: 'clear_view', label: 'We can see it whenever we want', weight: 0 },
                { key: 'takes_digging', label: 'We can work it out, but it takes digging', weight: 1 },
                { key: 'by_hand', label: 'We’d have to pull it together by hand', weight: 2 },
                { key: 'gut_feel', label: 'We’re going on gut feel for now', weight: 3 }
            ]
        }
    }
}];

export const DEPTH_QUESTIONS = [{
    id: 'priority',
    area: null,
    question: 'If one thing about how your business runs could be better tomorrow, what would you pick?',
    options: [
        { key: 'more_revenue', label: 'More customers or more revenue', scores: { capability: 1, visibility: 1 } },
        { key: 'less_manual', label: 'Less manual, repetitive work', scores: { flow: 3 } },
        { key: 'new_offering', label: 'Something new we could offer', scores: { capability: 3 } },
        { key: 'better_visibility', label: 'Better visibility into what’s working', scores: { visibility: 3 } },
        { key: 'less_dependence', label: 'Less dependence on any one person or tool', scores: { resilience: 3 } }
    ],
    variants: {
        // "more customers or more revenue" is not the lever before launch. The
        // other four mean what they mean for anyone, so they keep their keys
        startup: {
            question: 'If one thing about where you are could be better tomorrow, what would you pick?',
            options: [
                { key: 'in_front_of_users', label: 'Having it in front of real users', scores: { capability: 3 } },
                { key: 'less_manual', label: 'Less of it depending on me doing things by hand', scores: { flow: 3 } },
                { key: 'new_offering', label: 'Being able to offer something we can’t today', scores: { capability: 3 } },
                { key: 'better_visibility', label: 'Knowing what’s actually working', scores: { visibility: 3 } },
                { key: 'less_dependence', label: 'Less dependence on any one person or tool', scores: { resilience: 3 } }
            ]
        }
    }
},{
    id: 'history',
    area: null,
    question: 'Have you tried to improve this before, with software or a developer?',
    options: [
        { key: 'first_attempt', label: 'No, this would be our first attempt' },
        { key: 'off_the_shelf', label: 'We tried off-the-shelf tools but they didn’t fully fit', scores: { ceiling: 2 } },
        { key: 'hired_badly', label: 'We hired someone before and it didn’t go well' },
        { key: 'showing_age', label: 'We have some custom software but it’s showing its age', scores: { ceiling: 3 } }
    ],
    variants: {
        // "improve this" assumes there is a this. Same four answers, aimed at
        // something being built rather than something being run
        startup: {
            question: 'Have you tried to build this before, with software or a developer?',
            options: [
                { key: 'first_attempt', label: 'No, this would be our first attempt' },
                { key: 'off_the_shelf', label: 'We tried to put it together with off-the-shelf tools', scores: { ceiling: 2 } },
                { key: 'hired_badly', label: 'We hired someone before and it didn’t go well' },
                { key: 'showing_age', label: 'We built something ourselves and it won’t hold up', scores: { ceiling: 3 } }
            ]
        }
    }
},{
    id: 'fragility',
    area: 'resilience',
    question: 'If a key person or process were suddenly unavailable, how would the business hold up?',
    options: [
        { key: 'fine', label: 'We have backups and documentation, we’re fine', weight: 0 },
        { key: 'would_manage', label: 'It’d slow us down but we’d manage', weight: 1 },
        { key: 'real_problems', label: 'It’d cause real problems until they’re back', weight: 3 },
        { key: 'crisis', label: 'It would be a genuine crisis', weight: 3 }
    ],
    variants: {
        /* "A key person" is usually the founder reading the question, and asking
           it in the third person invites them to answer about somebody else.
           Asked about themselves it is the more useful question anyway. */
        startup: {
            question: 'If you were unavailable for a couple of weeks, how would this hold up?',
            options: [
                { key: 'fine', label: 'Someone else could keep it moving', weight: 0 },
                { key: 'would_manage', label: 'It’d stall, but nothing would break', weight: 1 },
                { key: 'real_problems', label: 'Customers would notice', weight: 3 },
                { key: 'crisis', label: 'Everything stops. It’s all me', weight: 3 }
            ]
        }
    }
},{
    id: 'readiness',
    area: null,
    question: 'When you think about investing to close the gap between where you are and where you want to be, where are you?',
    options: [
        { key: 'exploring', label: 'Just exploring for now' },
        { key: 'open_to_it', label: 'Open to it if the payoff is clear' },
        { key: 'actively_looking', label: 'Actively looking for the right person to help' },
        { key: 'need_to_act', label: 'It’s costing or limiting us enough that I need to act soon' }
    ],
    variants: {
        // "closing the gap" is abstract to a founder who can name the gap exactly
        startup: {
            question: 'When you think about paying someone to build this properly, where are you?',
            options: [
                { key: 'exploring', label: 'Just exploring for now' },
                { key: 'open_to_it', label: 'Open to it if the payoff is clear' },
                { key: 'actively_looking', label: 'Actively looking for the right person to help' },
                { key: 'need_to_act', label: 'It’s holding us back enough that I need to move soon' }
            ]
        }
    }
}];

export const ALL_QUESTIONS = [...CORE_QUESTIONS, ...DEPTH_QUESTIONS];

/* One opening per area, in three parts, which is the shape the results screen
   renders: `title` names the area in plain language, `detail` says what their
   answers suggest is happening, and `meaning` says what it means for them going
   forward. `detail` is built from the owner's own answers — a business
   re-entering data across several systems must never read the same paragraph as
   one with a working sync.

   Keep each part to a sentence or two. This is a pattern read, not a diagnosis,
   and length here is what makes it look generated in bulk.

   `action` is the free value: real advice the owner can act on this week
   without hiring anyone. Only the top opening's action is shown, so every one
   of these has to stand on its own as the single takeaway.

   `evidence` is the gate, and it is the most important field here. An opening
   is only ever named when the owner's own answer to its own question backs it
   up. The softer questions can push an area up the order; they can never put it
   on the screen by themselves. Two failures this prevents, both of which were
   real:

   - Telling someone their tools are setting their ceiling when they told us the
     tools are mostly fine. The detail line quotes their answer back, so the
     block ends up arguing with itself.
   - Naming an area whose question they never answered, which can only be
     written vaguely, and vague is exactly what makes a read look generated.

   Evidence names the keys it accepts — always the ones that count, never the
   ones that don't. Written as a negation it passes anything it has not heard of,
   so an answer naming a retired or mistyped key sails through the gate and the
   area surfaces on a detail line with nothing behind it. Listing the positives
   fails closed instead: a key nobody recognises backs nothing, and a new option
   has to be added here deliberately before it can put an area on the screen.

   Each list is the set of keys its `detail` map can speak to, across every
   variant. The two move together.

   An owner whose answers back nothing at all sees no areas, and that is the
   honest outcome — the screen still hands them an action and says plainly that
   nothing stood out. */
const OPPORTUNITIES = {
    capability: {
        evidence: answers => ['priority', 'pushed_aside', 'half_formed'].includes(answers.ambition),
        title: 'The work you haven’t gotten to yet',
        detail: answers => {
            const urgency = {
                priority: 'you called it a real priority',
                pushed_aside: 'you said it keeps getting pushed aside',
                half_formed: 'you have the shape of an idea rather than a finished thought'
            }[answers.ambition] || 'you have something in mind';
            return `There’s something you’ve wanted to build or offer, and ${urgency}.`;
        },
        meaning: 'That work is usually the highest-value work available to a business, and it’s the first thing the day-to-day takes. Not because it matters least, but because nothing forces it onto the calendar.',
        action: 'Write the idea down in two sentences: what it would let a customer do, and what has to be true for that to work. Most ideas that feel too big collapse into something small and buildable at that point, and the ones that don’t are the ones worth taking seriously.'
    },
    ceiling: {
        // a real ceiling, or working around their limits. Anything softer would
        // have us naming a limit they have just told us they do not feel
        evidence: answers => ['real_ceiling', 'work_around'].includes(answers.ceiling),
        title: 'Tools that are setting your ceiling',
        detail: answers => {
            const how = {
                real_ceiling: 'you said they’re a real ceiling',
                work_around: 'you said you work around their limits'
            }[answers.ceiling] || 'they get in the way';
            return `On what you’d like to do next, ${how}.`;
        },
        meaning: 'A limit you’ve worked around long enough stops feeling like a limit and starts feeling like the shape of the business. Naming it is what turns it back into a choice.',
        action: 'List three things you’d do this quarter if the tools allowed it. Any that your customers have already asked for go to the top. That’s a ranked build list and a reason to move, and the cost of the workaround is the honest budget for fixing it.',
        startup: {
            title: 'The gap between the idea and what you can build',
            detail: answers => {
                const how = {
                    real_ceiling: 'you know what you want, you just can’t build it',
                    work_around: 'you have something rough that won’t hold up'
                }[answers.ceiling] || 'it sits past what you can put together right now';
                return `On what you want to build, ${how}.`;
            },
            meaning: 'That gap is the whole question at your stage, and it is worth being precise about where it actually sits. Knowing which part you can’t build yourself is what turns an open-ended project into something with a first step and an end.',
            action: 'List the three things this has to do to be worth paying for, then mark which of them you could put in front of someone this month. The distance between those two lists is the real build, and it’s usually shorter than the version you’ve been carrying around.'
        }
    },
    flow: {
        // every answer that means something moves by hand, in either segment.
        // "syncs" and "nothing_yet" are absent because neither one does
        evidence: answers => [
            'by_hand', 're_enter', 'doesnt_arrive',
            'tools_talk', 'spreadsheets', 'held_together'
        ].includes(answers.movement),
        title: 'The same information, entered more than once',
        detail: answers => {
            const how = {
                by_hand: 'someone copies it across by hand',
                re_enter: 'the same details get re-entered in several places',
                doesnt_arrive: 'it often doesn’t make it everywhere at all'
            }[answers.movement] || 'it takes someone’s time to move';
            return `When a customer detail or an order has to live in more than one of your systems, ${how}.`;
        },
        meaning: 'That’s time your team spends standing in for software that isn’t talking to itself, and it’s usually where errors quietly creep in too. It’s also capacity you could be pointing at the year you described.',
        action: 'Pick the single piece of information your team re-enters most, and count how many places it gets typed. That number, times the times per day, is your clearest first target, and often the easiest to fix.',
        startup: {
            title: 'What you’re running on while you build',
            detail: answers => {
                const how = {
                    tools_talk: 'your off-the-shelf tools mostly talk to each other',
                    spreadsheets: 'you’re updating spreadsheets and docs by hand',
                    held_together: 'it’s a bit of everything, held together by hand'
                }[answers.movement] || 'it takes someone’s time to keep straight';
                return `Right now, ${how}.`;
            },
            meaning: 'That’s the right call this early. It costs nothing, and it shows you what you actually need before you pay to build it. It stops being the right call at the point where holding it together costs more than the work it is standing in for, and that point tends to arrive without announcing itself.',
            action: 'Pick the task you do by hand most often to keep this moving, and time yourself doing it once, honestly. That number is what the first piece of real software has to beat, and it’s usually smaller and more specific than it feels from the inside.'
        }
    },
    visibility: {
        evidence: answers => ['takes_digging', 'by_hand', 'gut_feel'].includes(answers.reporting),
        title: 'Answers you have to go and find',
        detail: answers => {
            const how = {
                takes_digging: 'you can get there, but it takes digging',
                by_hand: 'you have to ask someone or put it together by hand',
                gut_feel: 'you’re mostly going on gut feel'
            }[answers.reporting] || 'it takes work to find out';
            return `When you want a straight answer about how the business is doing, ${how}.`;
        },
        meaning: 'Decisions you’d make weekly with a live view tend to get made monthly without one. Closing that gap is usually a matter of surfacing numbers you already hold, not collecting new ones.',
        action: 'Write down the three numbers you’d want to see every Monday morning, then find where each one lives today. Any that take more than five minutes to produce are the reporting worth building first, and the exercise almost always shows the data already exists, which makes it a smaller job than owners expect.',
        startup: {
            title: 'Knowing whether it’s working',
            detail: answers => {
                const how = {
                    takes_digging: 'you can work it out, but it takes digging',
                    by_hand: 'you’d have to pull it together by hand',
                    gut_feel: 'you’re going on gut feel for now'
                }[answers.reporting] || 'it takes work to find out';
                return `When you want to know whether this is working, ${how}.`;
            },
            meaning: 'The decisions come fastest at this stage and the evidence for them is thinnest, which is exactly when going on instinct costs the most. The useful part is that deciding what you would need to see is almost always a smaller job than building what shows it.',
            action: 'Name the one number that would tell you this is working, before you build anything to measure it. If you can’t name it yet, that is the more useful finding of the two, and it’s worth an hour with somebody who has done this before you spend a month building.'
        }
    },
    resilience: {
        // depth-only, so most reads never reach it. "It'd slow us down but we'd
        // manage" is not a single point of failure and must not be sold as one
        evidence: answers => ['real_problems', 'crisis'].includes(answers.fragility),
        title: 'Work that only one person can do',
        detail: answers => {
            const how = {
                real_problems: 'it would cause real problems until they were back',
                crisis: 'it would be a genuine crisis'
            }[answers.fragility] || 'you pointed at how much rests on any one person or tool';
            return `If a key person or process were suddenly unavailable, ${how}.`;
        },
        meaning: 'That’s not a staffing issue. It means something the business runs on has never been written down or built into software. Turning it into a system is what lets you grow without the risk growing with you.',
        startup: {
            title: 'How much of this only you can do',
            detail: answers => {
                const how = {
                    real_problems: 'customers would notice',
                    crisis: 'everything stops, because it’s all you'
                }[answers.fragility] || 'a good deal of it rests on you';
                return `If you were unavailable for a couple of weeks, ${how}.`;
            },
            meaning: 'Early on that is unavoidable and not worth apologising for. What it does set is a ceiling: nothing here can be handed to anyone until it exists somewhere other than your head, and that becomes the constraint on hiring long before it becomes a risk.',
            action: 'Spend twenty minutes listing every task only you can do. That list usually splits in half. Part of it is a page of documentation you could write this month, and part of it genuinely has to be built. Most founders have never seen it written down.'
        },
        action: 'Ask that person to spend twenty minutes listing every task only they can do. That list usually splits in half: the part that’s documentation you can close this month for free, and the part that genuinely needs to be built. Most owners have never seen it written down.'
    }
};

/* An opening as this owner should read it. A segment override replaces whatever
   it names and inherits the rest, so an area whose question did not change for
   that segment needs no override at all — capability and resilience are asked
   the same way of everyone, and read the same way back. */
const opportunityFor = (key, answers) => {
    const opportunity = OPPORTUNITIES[key];
    const segment = segmentFor(answers);
    return segment && opportunity[segment] ? { ...opportunity, ...opportunity[segment] } : opportunity;
};

/* Ranks every answered question into its area and returns the two or three
   openings worth naming, strongest first. The scores exist to decide that
   order and go no further — nothing derived from them is shown to the owner. */
export const scoreAnswers = answers => {

    const buckets = {};
    const add = (area, value) => {
        if(!buckets[area]) buckets[area] = [];
        buckets[area].push(value);
    };

    ALL_QUESTIONS.forEach(base => {

        const key = answers[base.id];
        if(!key) return;

        // resolved first, because a startup's options are not the ones written
        // on the question — then matched by key, so the order of neither matters
        const question = resolveQuestion(base, answers);
        const option = findOption(question, key);

        // an answer naming an option that no longer exists costs itself and
        // nothing else
        if(!option) return;

        if(question.area && typeof option.weight === 'number') {
            add(question.area, option.weight);
        }
        // options that each point at a different area
        if(option.scores) {
            Object.keys(option.scores).forEach(area => add(area, option.scores[area]));
        }
    });

    const areas = Object.keys(buckets).map(key => {
        const values = buckets[key];
        return {
            key: key,
            name: AREAS[key],
            score: values.reduce((sum, value) => sum + value, 0) / values.length
        };
    }).sort((a, b) => b.score - a.score);

    /* Two or three, per the spec — enough to feel specific, few enough to stay
       pattern-level. A single opening is still worth showing on its own, and
       none at all is a real result rather than a failure: see `evidence` above,
       and the no-findings wording in buildReflection. */
    const findings = areas
        .filter(area => OPPORTUNITIES[area.key] && OPPORTUNITIES[area.key].evidence(answers))
        .slice(0, 3)
        .map(area => {
            const opportunity = opportunityFor(area.key, answers);
            return {
                key: area.key,
                name: area.name,
                title: opportunity.title,
                detail: opportunity.detail(answers),
                meaning: opportunity.meaning
            };
        });

    const top = findings.length > 0 ? findings[0] : null;

    return {
        action: top ? opportunityFor(top.key, answers).action : FALLBACK_ACTION,
        areas: areas,
        findings: findings,
        reflection: buildReflection(answers, findings),
        top: top
    };
};

/* Shown only if nothing at all cleared the floor, which takes an owner who
   answered at the good end of every question. They still leave with something
   real to do — no one finishes this tool empty-handed. */
const FALLBACK_ACTION = 'Write down the three numbers you’d want to see every Monday morning, then find where each one lives today. Any that take more than five minutes to produce are worth building a view for, and the exercise almost always shows the data already exists.';

/* The one line under the headline. It leads with where they said they want to
   go, so the read opens on their aspiration rather than on a verdict about
   their business, then hands off to the openings below. Two sentences at most:
   this is the line that proves the tool listened, and it stops being that the
   moment it turns into a paragraph. */
const buildReflection = (answers, findings) => {

    /* One map for both segments. Where a variant introduces its own answers it
       introduces its own keys, so there is nothing to disambiguate — which is
       the whole point of storing the key instead of the position. */
    const horizon = {
        more_customers: 'taking on more customers without more chaos',
        new_offering: 'offering something you can’t offer today',
        more_from_tools: 'getting more out of the tools and data you already have',
        free_up_time: 'freeing up your time and your team’s',
        run_smoothly: 'running more smoothly across locations and the whole team',
        build_it: 'having it built and in front of real users',
        prove_it: 'knowing the idea works before you spend more on it',
        charge_for_it: 'being able to charge for it',
        less_on_me: 'not having every part of it depend on you',
        show_investors: 'having something solid enough to show investors or partners'
    }[answers.horizon];

    const business = {
        service: 'a service business',
        franchise: 'a multi-location operation',
        professional: 'a professional services firm',
        retail: 'a retail or product business',
        startup: 'a startup'
    }[answers.business] || 'a business';

    const opening = horizon
        ? `You run ${business}, and the year ahead you described is about ${horizon}.`
        : `You run ${business}, and you’re weighing where it could go next.`;

    if(findings.length === 0) {
        return `${opening} Nothing in your answers points at an obvious problem standing in the way, which is a good place to be starting from.`;
    }

    const count = findings.length === 1 ? 'One thing' : `${findings.length === 2 ? 'Two' : 'Three'} things`;
    return `${opening} ${count} in your answers stand${findings.length === 1 ? 's' : ''} out as the shortest way there.`;
};

/* The structured payload for the server-side model, in the shape the priming
   spec expects: labelled answers plus the deterministic read, so the model
   reasons over real input and can never respond to a blank prompt. */
export const buildModelPayload = (answers, freeText) => {
    const result = scoreAnswers(answers);
    return {
        /* Resolved, so the model is handed the question the owner was asked and
           the option they picked — not the default wording of either. It quotes
           this back verbatim, so anything lost here comes out as a read about a
           business they do not recognise.

           The key rides along beside the prose. It is what makes the stored
           answers worth querying later: `movement = re_enter` counts across
           every owner who ever said it, where the sentence has to be read. */
        answers: ALL_QUESTIONS.reduce((payload, base) => {

            const key = answers[base.id];
            if(!key) return payload;

            const question = resolveQuestion(base, answers);
            const option = findOption(question, key);
            if(!option) return payload;

            payload[base.id] = {
                key: option.key,
                question: question.question,
                answer: option.label
            };
            return payload;
        }, {}),
        notes: freeText || null,
        pattern: {
            /* Ranked, not scored. The model is told which openings the answers
               point at and in what order, and is left to reason about them —
               handing it a number invites it to quote one back, and a number in
               the readout is the report card this tool is built to avoid. */
            ranked: result.areas.map(area => area.name),
            surfaced: result.findings.map(finding => ({
                area: finding.name,
                title: finding.title
            }))
        }
    };
};

export const depthAnswered = answers => DEPTH_QUESTIONS.some(question => answers[question.id] !== undefined);
